type NextNodePiiFieldType = "NAME" | "EMAIL" | "PHONE";

const NEXT_NODE_PII_TOKEN_PREFIX = "__SDUI_PII_V1__";
const NEXT_NODE_PII_AAD_PREFIX = "sdui-service:next-node-pii:v1:";
const NEXT_NODE_PII_TOKEN_REGEX =
  /__SDUI_PII_V1__:(NAME|EMAIL|PHONE):([A-Za-z0-9_-]+)/g;
const VERSION_1 = 1;
const IV_LENGTH_BYTES = 12;
const AUTH_TAG_LENGTH_BYTES = 16;
const ENCRYPTION_ENABLED_VALUE = "true";

export const isNextNodePiiDecryptionEnabled = () => {
  return (
    process.env.NEXT_PUBLIC_NEXT_NODE_PII_DECRYPTION_ENABLED ===
    ENCRYPTION_ENABLED_VALUE
  );
};

export const decryptNextNodePiiResponse = async <T>(response: T): Promise<T> => {
  if (!isNextNodePiiDecryptionEnabled()) {
    return response;
  }

  const key = await resolveAesKey();
  return (await decryptNode(response, key)) as T;
};

export const decryptPiiTokensInString = async (
  value: string,
  keyBase64 = process.env.NEXT_PUBLIC_NEXT_NODE_PII_AES_KEY_BASE64
): Promise<string> => {
  if (!value || !value.includes(NEXT_NODE_PII_TOKEN_PREFIX)) {
    return value;
  }

  const key = await resolveAesKey(keyBase64);
  return decryptStringValue(value, key);
};

const decryptNode = async (value: any, key: CryptoKey): Promise<any> => {
  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => decryptNode(item, key)));
  }

  if (value && typeof value === "object") {
    const decryptedObject = { ...value };
    const keys = Object.keys(decryptedObject);
    for (let index = 0; index < keys.length; index += 1) {
      const keyName = keys[index];
      decryptedObject[keyName] = await decryptNode(decryptedObject[keyName], key);
    }
    return decryptedObject;
  }

  if (typeof value !== "string" || !value.includes(NEXT_NODE_PII_TOKEN_PREFIX)) {
    return value;
  }

  return decryptStringValue(value, key);
};

const decryptStringValue = async (
  value: string,
  key: CryptoKey
): Promise<string> => {
  const regex = new RegExp(NEXT_NODE_PII_TOKEN_REGEX);
  let decryptedValue = "";
  let lastIndex = 0;
  let match = regex.exec(value);

  while (match) {
    decryptedValue += value.slice(lastIndex, match.index);
    decryptedValue += await decryptPiiToken(match[0], key);
    lastIndex = match.index + match[0].length;
    match = regex.exec(value);
  }

  decryptedValue += value.slice(lastIndex);
  return decryptedValue;
};

const decryptPiiToken = async (
  token: string,
  key: CryptoKey
): Promise<string> => {
  const tokenData = parsePiiToken(token);
  if (!tokenData) {
    throw new Error("Invalid next-node PII token");
  }

  const packed = base64UrlToBytes(tokenData.payload);
  if (packed.length <= 1 + IV_LENGTH_BYTES + AUTH_TAG_LENGTH_BYTES) {
    throw new Error("Invalid next-node PII token payload");
  }

  const version = packed[0];
  if (version !== VERSION_1) {
    throw new Error(`Unsupported next-node PII token version: ${version}`);
  }

  const iv = packed.slice(1, 1 + IV_LENGTH_BYTES);
  const ciphertextAndTag = packed.slice(1 + IV_LENGTH_BYTES);
  const plaintext = await getSubtleCrypto().decrypt(
    {
      name: "AES-GCM",
      iv,
      tagLength: AUTH_TAG_LENGTH_BYTES * 8,
      additionalData: getAad(tokenData.type)
    },
    key,
    ciphertextAndTag
  );

  return new TextDecoder().decode(plaintext);
};

const resolveAesKey = async (
  keyBase64 = process.env.NEXT_PUBLIC_NEXT_NODE_PII_AES_KEY_BASE64
): Promise<CryptoKey> => {
  if (!keyBase64 || !keyBase64.trim()) {
    throw new Error(
      "NEXT_PUBLIC_NEXT_NODE_PII_AES_KEY_BASE64 must be configured for next-node PII decryption"
    );
  }

  const rawKey = base64ToBytes(keyBase64.trim());
  if (rawKey.length !== 32) {
    throw new Error(
      "NEXT_PUBLIC_NEXT_NODE_PII_AES_KEY_BASE64 must decode to exactly 32 bytes for AES-256-GCM"
    );
  }

  return getSubtleCrypto().importKey("raw", rawKey, "AES-GCM", false, [
    "decrypt"
  ]);
};

const parsePiiToken = (
  token: string
): { type: NextNodePiiFieldType; payload: string } | null => {
  const match = token.match(
    /^__SDUI_PII_V1__:(NAME|EMAIL|PHONE):([A-Za-z0-9_-]+)$/
  );
  if (!match) {
    return null;
  }

  return {
    type: match[1] as NextNodePiiFieldType,
    payload: match[2]
  };
};

const getAad = (type: NextNodePiiFieldType): Uint8Array => {
  return new TextEncoder().encode(`${NEXT_NODE_PII_AAD_PREFIX}${type}`);
};

const getSubtleCrypto = (): SubtleCrypto => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error("Web Crypto API is required for next-node PII decryption");
  }

  return subtle;
};

const base64UrlToBytes = (value: string): Uint8Array => {
  return base64ToBytes(value.replace(/-/g, "+").replace(/_/g, "/"));
};

const base64ToBytes = (value: string): Uint8Array => {
  const paddingLength = (4 - (value.length % 4)) % 4;
  const paddedValue = `${value}${"=".repeat(paddingLength)}`;
  const binary = atob(paddedValue);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};
